import React, { useState, useCallback, useRef, useEffect } from 'react';
import { cn, extractErrorMessage, getUploadUrl } from '@/lib/utils';
import { uploadApi } from '@/lib/api-client';
import { toast } from 'sonner';
import { UploadCloud, X, Loader2 } from 'lucide-react';

export function extractClipboardImageFile(
    clipboardData?: DataTransfer | null,
): File | null {
    if (!clipboardData) return null;
    const items = clipboardData.items;
    if (items && items.length > 0) {
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item && typeof item.type === 'string' && item.type.startsWith('image/')) {
                const file = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
                if (file) return file;
            }
        }
    }
    const files = clipboardData.files;
    if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
            const f = files[i];
            if (f && typeof f.type === 'string' && f.type.startsWith('image/')) {
                return f;
            }
        }
    }
    return null;
}

export function shouldHandleWindowImagePaste(params: {
    disabled?: boolean;
    uploading?: boolean;
    hasImageInClipboard: boolean;
    isTopmostDialogScope: boolean;
}): boolean {
    if (params.disabled || params.uploading) return false;
    if (!params.hasImageInClipboard) return false;
    if (!params.isTopmostDialogScope) return false;
    return true;
}

export function resolveDropzonePreviewSrc(src?: string | null): string {
    if (!src) return '';
    if (src.startsWith('data:') || src.startsWith('blob:')) return src;
    return getUploadUrl(src);
}

async function compressImage(dataUrl: string, maxSizeKB = 2000): Promise<string> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width || 800;
            let height = img.height || 800;
            const MAX_DIM = 1200;
            if (width > height) {
                if (width > MAX_DIM) { height *= MAX_DIM / width; width = MAX_DIM; }
            } else {
                if (height > MAX_DIM) { width *= MAX_DIM / height; height = MAX_DIM; }
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                resolve(dataUrl);
                return;
            }
            ctx.drawImage(img, 0, 0, width, height);
            let quality = 0.9;
            let res = canvas.toDataURL('image/jpeg', quality);
            while (res.length / 1024 > maxSizeKB && quality > 0.1) {
                quality -= 0.1;
                res = canvas.toDataURL('image/jpeg', quality);
            }
            resolve(res);
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
    });
}

interface ImageDropzoneProps {
    value?: string;
    onChange: (url: string) => void;
    className?: string;
    maxSizeKB?: number;
    disabled?: boolean;
}

export function ImageDropzone({
    value,
    onChange,
    className,
    maxSizeKB = 2048,
    disabled = false,
}: ImageDropzoneProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [localPreview, setLocalPreview] = useState<string | null>(value || null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync external value changes
    useEffect(() => {
        if (value && !value.startsWith('data:')) {
            setLocalPreview(value);
        } else if (!value) {
            setLocalPreview(null);
        }
    }, [value]);

    const processAndUploadFile = useCallback(async (file: File) => {
        setIsUploading(true);
        try {
            // Read to base64
            const reader = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
                reader.onload = (ev) => resolve(ev.target?.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

            // Compress
            const compressed = await compressImage(dataUrl, maxSizeKB);
            setLocalPreview(compressed); // Optimistic preview

            // Convert back to file for upload
            const arr = compressed.split(',');
            const mimeMatch = arr[0].match(/:(.*?);/);
            const mime = mimeMatch ? mimeMatch[1] : file.type || 'image/jpeg';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) { u8arr[n] = bstr.charCodeAt(n); }
            const compressedFile = new File([u8arr], file.name || "image.jpg", { type: mime });

            // Upload
            const { fileUrl } = await uploadApi.uploadFile(compressedFile);
            setLocalPreview(fileUrl);
            onChange(fileUrl);
        } catch (error: any) {
            console.error('Upload error', error);
            toast.error('Lỗi tải ảnh lên server: ' + extractErrorMessage(error));
            setLocalPreview(value || null); // Revert
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }, [maxSizeKB, onChange, value]);

    // Handle Drag & Drop
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (isUploading) return;
        const files = e.dataTransfer.files;
        if (files && files.length > 0 && files[0].type.startsWith('image/')) {
            processAndUploadFile(files[0]);
        } else if (files && files.length > 0) {
            toast.error('Chỉ hỗ trợ file hình ảnh!');
        }
    };

    // Handle Input Change
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            processAndUploadFile(files[0]);
        }
    };

    // Handle Global Paste (supports Ctrl+V / Cmd+V even when focus is inside <input> or <textarea> of the active modal)
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (e.defaultPrevented) return;
            const imageFile = extractClipboardImageFile(e.clipboardData);

            const el = containerRef.current;
            const myDialog = el?.closest('[role="dialog"]') || null;
            const openDialogs = Array.from(document.querySelectorAll('[role="dialog"]'));
            const topmostDialog = openDialogs.length > 0 ? openDialogs[openDialogs.length - 1] : null;
            const isTopmostDialogScope = topmostDialog ? myDialog === topmostDialog : true;

            if (
                !shouldHandleWindowImagePaste({
                    disabled,
                    uploading: isUploading,
                    hasImageInClipboard: Boolean(imageFile),
                    isTopmostDialogScope,
                })
            ) {
                return;
            }

            if (imageFile) {
                e.preventDefault();
                processAndUploadFile(imageFile);
            }
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [disabled, isUploading, processAndUploadFile]);

    const handleDirectPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (disabled || isUploading) return;
        const imageFile = extractClipboardImageFile(e.clipboardData as unknown as DataTransfer);
        if (imageFile) {
            e.preventDefault();
            e.stopPropagation();
            processAndUploadFile(imageFile);
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setLocalPreview(null);
        onChange('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const previewSrc = resolveDropzonePreviewSrc(localPreview);

    return (
        <div
            ref={containerRef}
            tabIndex={disabled ? -1 : 0}
            data-testid="image-dropzone"
            className={cn(
                'relative flex flex-col items-center justify-center border-2 border-dashed rounded-lg transition-all overflow-hidden group w-full focus:outline-none focus:ring-2 focus:ring-indigo-500/50',
                isDragging ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100',
                disabled && 'opacity-60 pointer-events-none',
                className
            )}
            onPaste={handleDirectPaste}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!disabled && !isUploading) fileInputRef.current?.click();
                }
            }}
        >
            <input 
                ref={fileInputRef} 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleFileSelect} 
                disabled={disabled || isUploading}
            />

            {localPreview ? (
                <>
                    <img 
                        src={previewSrc} 
                        alt="Preview" 
                        className={cn("w-full h-full object-contain p-1", isUploading && "opacity-50 blur-sm")} 
                    />
                    {!isUploading && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-red-100 text-slate-500 hover:text-red-600 rounded-full shadow-sm backdrop-blur-md transition-colors opacity-0 group-hover:opacity-100"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </>
            ) : (
                <div className="flex flex-col items-center justify-center p-6 text-center text-slate-500 w-full h-full">
                    <div className="w-10 h-10 mb-3 rounded-full bg-indigo-50 text-indigo-500 flex items-center justify-center group-hover:scale-110 group-hover:bg-indigo-100 transition-transform">
                        <UploadCloud className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-medium text-slate-700">Kéo thả ảnh vào đây hoặc nhấn <kbd className="px-1.5 py-0.5 text-xs rounded-md bg-slate-100 border border-slate-200">Ctrl+V</kbd></p>
                    <p className="text-[11px] text-slate-500 mt-2">
                        hoặc 
                        <button 
                            type="button" 
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isUploading) fileInputRef.current?.click();
                            }}
                            className="text-indigo-600 hover:text-indigo-700 hover:underline mx-1 font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/50 rounded px-1"
                        >
                            chọn file
                        </button>
                        từ máy tính
                    </p>
                    <p className="text-[10px] font-mono text-slate-400 mt-3 border border-slate-200 px-2.5 py-0.5 rounded-full bg-white shadow-sm">Tối đa 2MB</p>
                </div>
            )}

            {isUploading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[2px] z-10">
                    <Loader2 className="w-6 h-6 text-indigo-600 animate-spin mb-2" />
                    <span className="text-xs font-semibold text-indigo-700">Đang tải lên...</span>
                </div>
            )}
        </div>
    );
}
